import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HeroMetricRow from '../components/incidents/HeroMetricRow';
import HotspotsCard from '../components/incidents/HotspotsCard';
import RecurringRiskHeatmapCard from '../components/incidents/RecurringRiskHeatmapCard';
import { buildScopedIncidentsPath } from '../utils/incidentsNavigation';
import {
  buildFlowHighlights,
  buildGroupBreakdown,
  buildIncidentOverviewMetrics,
  buildPercentBreakdown,
  deriveDateRangeFromAnalyticsRange,
  formatCategoryName,
  type AnalyticsTimeRange,
} from './analytics/incidentAnalyticsModel';
import { computePackCoverageAnalytics, PACK_COVERAGE_UNKNOWN_KEY } from './analytics/packCoverageModel';
import { useIncidentAnalyticsOverview } from './analytics/useIncidentAnalyticsOverview';
import { presentAreaLabel } from '../utils/presentAreaLabel';
import {
  isIncidentGroupRecurring,
  getIncidentGroupLookbackSignalCount,
} from '../domain/incidents/incidentPatternSemantics';
import {
  buildOutcomeBoundarySummary,
  buildScopeBoundarySummary,
} from '../domain/incidents/sourceContextSemantics';

const COLORS = ['#1d4f91', '#2f855a', '#ed6c02', '#7b61ff', '#00acc1', '#d32f2f'];
/** Non-urgent palette for category mix; keep red/orange for severity and ops elsewhere */
const INCIDENT_TYPE_PIE_COLORS = ['#64748b', '#94a3b8', '#cbd5e1', '#475569', '#94a3b8', '#334155', '#1e293b', '#78909c'];

const ZONE_COLORS: Record<string, string> = {};

function parseRange(value: string | null): AnalyticsTimeRange {
  return value === '1h' || value === '6h' || value === '7d' ? value : '24h';
}

function getDataKeys(data: Array<Record<string, unknown>>): string[] {
  if (!Array.isArray(data) || !data.length) return [];
  const keys = new Set<string>();
  data.forEach((point) => {
    Object.keys(point || {}).forEach((key) => {
      if (key !== 'time' && key !== 'timestamp') keys.add(key);
    });
  });
  return Array.from(keys).sort();
}

function filterSeriesData(data: Array<Record<string, unknown>>, selectedZone: string): Array<Record<string, unknown>> {
  if (selectedZone === 'all') return data;
  return data.map((point) => ({
    time: point.time,
    timestamp: point.timestamp,
    [selectedZone]: point[selectedZone] ?? 0,
  }));
}

const Analytics: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<AnalyticsTimeRange>(() => parseRange(searchParams.get('range')));
  const [selectedZone, setSelectedZone] = useState<string>(() => (searchParams.get('zone_id') || 'all').trim() || 'all');
  const [heatmapDimension, setHeatmapDimension] = useState<'zone' | 'camera'>('zone');
  const [heatmapIncidentType, setHeatmapIncidentType] = useState<string>('');

  const {
    loading,
    error,
    kpis,
    opsQueues,
    groups,
    incidentFlowData,
    severityData,
    categoryData,
    zoneOccupancyData,
    heatmap,
    safetySummary,
    zoneOptions,
    zoneLabels,
    zoneStatuses,
    cameraNameById: cameraNameByIdFromRef,
    sitePolicies,
    siteZones,
    reviewOutcomeRollup,
    reviewOutcomeHistory,
    ownershipRollup,
    correctiveActionRollup,
    measurementRollup,
    actionSnapshotRollup,
    escalationRollup,
  } = useIncidentAnalyticsOverview({
    timeRange,
    selectedZone,
    heatmapDimension,
    heatmapIncidentType,
  });

  useEffect(() => {
    const nextRange = parseRange(searchParams.get('range'));
    const nextZone = (searchParams.get('zone_id') || 'all').trim() || 'all';
    if (nextRange !== timeRange) setTimeRange(nextRange);
    if (nextZone !== selectedZone) setSelectedZone(nextZone);
  }, [searchParams, selectedZone, timeRange]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (timeRange === '24h') next.delete('range');
    else next.set('range', timeRange);
    if (selectedZone === 'all') next.delete('zone_id');
    else next.set('zone_id', selectedZone);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, selectedZone, setSearchParams, timeRange]);

  const getZoneColor = useCallback((key: string, index: number) => {
    return ZONE_COLORS[key] || COLORS[index % COLORS.length];
  }, []);

  const getSeriesLabel = useCallback((value: string) => {
    if (!value) return 'Unknown';
    if (zoneLabels[value]) return presentAreaLabel(zoneLabels[value]);
    return presentAreaLabel(formatCategoryName(value));
  }, [zoneLabels]);

  const zoneNameById = useMemo(() => {
    const map = new Map<string, string>();
    zoneOptions.forEach((zoneId) => {
      map.set(zoneId, presentAreaLabel(getSeriesLabel(zoneId)));
    });
    zoneStatuses.forEach((zone) => {
      const zoneId = String(zone.zone_id || '').trim();
      const label = presentAreaLabel(String(zone.name || '').trim());
      if (zoneId && label) map.set(zoneId, label);
    });
    return map;
  }, [getSeriesLabel, zoneOptions, zoneStatuses]);

  const overviewMetrics = useMemo(
    () =>
      buildIncidentOverviewMetrics({
        kpis,
        opsQueues,
        groups,
        fallbackToKpisWhenNoGroups: selectedZone === 'all',
      }),
    [groups, kpis, opsQueues, selectedZone],
  );

  const severityBreakdown = useMemo(() => {
    const fromGroups = buildGroupBreakdown(groups, 'severity');
    return buildPercentBreakdown(fromGroups.length ? fromGroups : severityData);
  }, [groups, severityData]);

  const incidentTypeBreakdown = useMemo(() => {
    const fromGroups = buildGroupBreakdown(groups, 'incident_type');
    return buildPercentBreakdown(fromGroups.length ? fromGroups : categoryData);
  }, [categoryData, groups]);

  const flowHighlights = useMemo(() => buildFlowHighlights(incidentFlowData), [incidentFlowData]);
  const peakClose = useMemo(() => {
    return incidentFlowData.reduce((peak, point) => {
      const closed = Number(point.closed || 0);
      return !peak || closed > peak.closed ? { time: point.time, closed } : peak;
    }, null as { time: string; closed: number } | null);
  }, [incidentFlowData]);
  const availableZones = useMemo(() => {
    const union = new Set<string>(zoneOptions);
    getDataKeys(zoneOccupancyData).forEach((key) => union.add(key));
    groups.forEach((group) => {
      const zoneId = String(group.zone_id || '').trim();
      if (zoneId) union.add(zoneId);
    });
    return Array.from(union).sort();
  }, [groups, zoneOccupancyData, zoneOptions]);
  const cameraNameById = useMemo(() => {
    const map = new Map<string, string>();
    Object.entries(cameraNameByIdFromRef || {}).forEach(([id, label]) => {
      if (id && label) map.set(id, String(label));
    });
    groups.forEach((group) => {
      const cameraId = String(group.camera_id || '').trim();
      if (cameraId && !map.has(cameraId)) map.set(cameraId, cameraId);
    });
    return map;
  }, [cameraNameByIdFromRef, groups]);
  const heatmapIncidentTypeOptions = useMemo(() => {
    return incidentTypeBreakdown.map((item) => ({
      value: item.name,
      label: item.label,
    }));
  }, [incidentTypeBreakdown]);
  const filteredZoneData = useMemo(() => filterSeriesData(zoneOccupancyData, selectedZone), [selectedZone, zoneOccupancyData]);
  const occupancyKeys = useMemo(() => getDataKeys(filteredZoneData), [filteredZoneData]);
  const occupancySignalFlatZero = useMemo(
    () =>
      occupancyKeys.length > 0 &&
      filteredZoneData.length > 0 &&
      filteredZoneData.every((point) => occupancyKeys.every((key) => Number(point?.[key] || 0) === 0)),
    [filteredZoneData, occupancyKeys],
  );
  const highestOccupancy = useMemo(() => {
    if (!filteredZoneData.length || !occupancyKeys.length) return null;
    const latest = filteredZoneData[filteredZoneData.length - 1];
    const ranked = occupancyKeys
      .map((key) => ({ zone: key, value: Number(latest?.[key] || 0) }))
      .sort((a, b) => b.value - a.value);
    return ranked[0] && ranked[0].value > 0 ? ranked[0] : null;
  }, [filteredZoneData, occupancyKeys]);

  const buildIncidentsUrl = useCallback((extraParams?: Record<string, string | null | undefined>) => {
    const path = buildScopedIncidentsPath('/incidents');
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(path, origin);
    const { fromDate, toDate } = deriveDateRangeFromAnalyticsRange(timeRange);
    url.searchParams.set('from_date', fromDate);
    url.searchParams.set('to_date', toDate);
    url.searchParams.set('table_mode', 'grouped');
    if (selectedZone !== 'all') url.searchParams.set('zone_id', selectedZone);
    Object.entries(extraParams || {}).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') url.searchParams.delete(key);
      else url.searchParams.set(key, String(value));
    });
    return `${url.pathname}${url.search}`;
  }, [selectedZone, timeRange]);

  const telemetryCards = useMemo(
    () => [
      {
        label: 'Cameras Online',
        value: safetySummary.cameras_online,
        description: 'Live telemetry from the safety monitoring footprint.',
      },
      {
        label: 'Live Alert Signals',
        value:
          typeof safetySummary.prometheus_firing_alerts === 'number'
            ? safetySummary.prometheus_firing_alerts
            : safetySummary.active_alerts,
        description: 'Prometheus or live signal count, kept separate from incident workflow volume.',
      },
      {
        label: 'Average Pipeline Latency',
        value: safetySummary.avg_latency_ms > 0 ? `${Math.round(safetySummary.avg_latency_ms)} ms` : 'No live metric',
        description: 'Operational telemetry, not incident queue state.',
      },
      {
        label: 'Highest Occupancy',
        value: highestOccupancy ? `${getSeriesLabel(highestOccupancy.zone)} (${highestOccupancy.value.toFixed(1)})` : 'No live occupancy metric',
        description: 'Current occupancy leader for the visible scope.',
      },
    ],
    [getSeriesLabel, highestOccupancy, safetySummary],
  );
  const topRecurringGroup = useMemo(() => {
    return [...groups]
      .filter((group) => isIncidentGroupRecurring(group))
      .sort((a, b) => getIncidentGroupLookbackSignalCount(b) - getIncidentGroupLookbackSignalCount(a))[0] || null;
  }, [groups]);
  const topRecurringLabel = useMemo(() => {
    if (!topRecurringGroup) return '';
    const zoneId = String(topRecurringGroup.zone_id || '').trim();
    const cameraId = String(topRecurringGroup.camera_id || '').trim();
      if (zoneId) return presentAreaLabel(zoneNameById.get(zoneId) || getSeriesLabel(zoneId));
    if (cameraId) return cameraNameById.get(cameraId) || cameraId;
    return 'the current site';
  }, [cameraNameById, getSeriesLabel, topRecurringGroup, zoneNameById]);
  const operationalReadout = useMemo(() => {
    const confirmedRisk = Number(reviewOutcomeRollup?.customer_risk_total || 0);
    const uncertainRisk = Number(
      Array.isArray(reviewOutcomeRollup?.risk_disposition_items)
        ? ((reviewOutcomeRollup?.risk_disposition_items.find((item) => String(item?.risk_disposition || '') === 'uncertain_risk')?.count) || 0)
        : 0,
    );
    const excludedRisk = Number(
      Array.isArray(reviewOutcomeRollup?.risk_disposition_items)
        ? ((reviewOutcomeRollup?.risk_disposition_items.find((item) => String(item?.risk_disposition || '') === 'excluded_from_risk')?.count) || 0)
        : 0,
    );
    if (overviewMetrics.recurringItems > 0 && topRecurringLabel) {
      const base = `Recurring signals are highest in ${topRecurringLabel}; ${overviewMetrics.recurringLocations || 1} high-risk area${
        overviewMetrics.recurringLocations === 1 ? '' : 's'
      } show recurring signals.`;
      if (confirmedRisk > 0 || uncertainRisk > 0 || excludedRisk > 0) {
        return `${base} Review status currently classifies ${confirmedRisk} as confirmed safety concern${confirmedRisk === 1 ? '' : 's'}, ${uncertainRisk} as evidence-limited, and ${excludedRisk} as non-risk activity.`;
      }
      return base;
    }
    if (overviewMetrics.highRiskUnresolved > 0) {
      return `${overviewMetrics.highRiskUnresolved} critical risks open.`;
    }
    if (overviewMetrics.openIncidents > 0) {
      return `${overviewMetrics.openIncidents} open across ${Math.max(overviewMetrics.openZones, 1)} zone${
        overviewMetrics.openZones === 1 ? '' : 's'
      }.`;
    }
    return 'No single recurring risk is dominating this window.';
  }, [overviewMetrics, reviewOutcomeRollup, topRecurringLabel]);
  const reviewTrendReadout = useMemo(() => {
    const history = Array.isArray(reviewOutcomeHistory?.history) ? (reviewOutcomeHistory?.history ?? []) : [];
    if (!history.length) return null;
    const countDisposition = (bucket: any, disposition: string) =>
      Number(
        Array.isArray(bucket?.risk_disposition_items)
          ? ((bucket.risk_disposition_items.find((item: any) => String(item?.risk_disposition || '') === disposition)?.count) || 0)
          : 0,
      );
    const latest = history[history.length - 1];
    const previous = history.length > 1 ? history[history.length - 2] : null;
    const latestConfirmed = Number(latest?.customer_risk_total || 0);
    const latestUncertain = countDisposition(latest, 'uncertain_risk');
    const latestExcluded = countDisposition(latest, 'excluded_from_risk');
    if (!previous) {
      return `Latest review bucket shows ${latestConfirmed} confirmed safety concern${latestConfirmed === 1 ? '' : 's'}, ${latestUncertain} evidence-limited review${latestUncertain === 1 ? '' : 's'}, and ${latestExcluded} reviewed non-risk activit${latestExcluded === 1 ? 'y' : 'ies'}.`;
    }
    const prevConfirmed = Number(previous?.customer_risk_total || 0);
    const prevUncertain = countDisposition(previous, 'uncertain_risk');
    const prevExcluded = countDisposition(previous, 'excluded_from_risk');
    return `Confirmed safety concerns moved from ${prevConfirmed} to ${latestConfirmed}; evidence-limited review moved from ${prevUncertain} to ${latestUncertain}; reviewed non-risk activity moved from ${prevExcluded} to ${latestExcluded}.`;
  }, [reviewOutcomeHistory]);
  const reviewBoundaryReadout = useMemo(
    () => buildScopeBoundarySummary(reviewOutcomeRollup, 'incidents in this range'),
    [reviewOutcomeRollup],
  );
  const measurementBoundaryReadout = useMemo(
    () =>
      buildOutcomeBoundarySummary(
        measurementRollup,
        Number(measurementRollup?.measured_total || 0),
        'this range',
      ),
    [measurementRollup],
  );
  const showIncidentTypeMix = incidentTypeBreakdown.length > 1;
  const showSeverityMix = severityBreakdown.length > 1;
  const hasMeaningfulTelemetry = useMemo(() => {
    return Boolean(
      (typeof safetySummary.avg_latency_ms === 'number' && safetySummary.avg_latency_ms > 0) ||
      (typeof safetySummary.prometheus_firing_alerts === 'number' && safetySummary.prometheus_firing_alerts > 0) ||
      (typeof safetySummary.active_alerts === 'number' && safetySummary.active_alerts > 0) ||
      highestOccupancy ||
      (occupancyKeys.length > 0 && !occupancySignalFlatZero),
    );
  }, [highestOccupancy, occupancyKeys.length, occupancySignalFlatZero, safetySummary.active_alerts, safetySummary.avg_latency_ms, safetySummary.prometheus_firing_alerts]);
  const secondaryMetricCards = useMemo(
    () => [
      {
        key: 'my_queue',
        label: 'My Queue',
        value: overviewMetrics.myQueueCount,
        context: 'Assigned operational follow-up',
        tone: 'default' as const,
        params: { table_mode: 'raw', ops_queue: 'my_queue' },
      },
      {
        key: 'overdue',
        label: 'Overdue Reviews',
        value: overviewMetrics.overdueCount,
        context: 'Past the current review threshold',
        tone: overviewMetrics.overdueCount > 0 ? ('danger' as const) : ('default' as const),
        params: { table_mode: 'raw', ops_queue: 'overdue' },
      },
      {
        key: 'escalations',
        label: 'Escalations',
        value: overviewMetrics.escalationsCount,
        context: 'Incidents meeting escalation policy',
        tone: overviewMetrics.escalationsCount > 0 ? ('warning' as const) : ('default' as const),
        params: { table_mode: 'raw', ops_queue: 'escalations' },
      },
      {
        key: 'unassigned',
        label: 'Unassigned High-Risk',
        value: overviewMetrics.unassignedHighRiskCount,
        context: 'High-risk incidents without a follow-up owner',
        tone: overviewMetrics.unassignedHighRiskCount > 0 ? ('danger' as const) : ('default' as const),
        params: { table_mode: 'raw', ops_queue: 'unassigned_high_risk' },
      },
    ],
    [overviewMetrics],
  );
  const showSecondaryMetricRow = useMemo(
    () => secondaryMetricCards.some((card) => Number(card.value || 0) > 0),
    [secondaryMetricCards],
  );
  const timeRangeLabel = useMemo(() => {
    if (timeRange === '1h') return 'Last hour';
    if (timeRange === '6h') return 'Last 6 hours';
    if (timeRange === '7d') return 'Last 7 days';
    return 'Last 24 hours';
  }, [timeRange]);

  const packCoverage = useMemo(
    () =>
      computePackCoverageAnalytics({
        groups,
        policies: sitePolicies,
        zones: siteZones,
      }),
    [groups, sitePolicies, siteZones],
  );

  return (
    <Box
      sx={{
        /* Use full main column width; cancel parent content padding on sm+ for a wider chart canvas */
        width: { xs: '100%', sm: 'calc(100% + 48px)' },
        maxWidth: 'none',
        minWidth: 0,
        ml: { sm: -3 },
        mr: { sm: -3 },
        boxSizing: 'border-box',
        px: { sm: 3 },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={3} flexWrap="wrap">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Risk Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, maxWidth: 'min(100%, 1100px)' }}>
            Live incident load, what is repeating, and how queues look for this site.
          </Typography>
        </Box>
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Zone</InputLabel>
            <Select value={selectedZone} label="Zone" onChange={(event) => setSelectedZone(String(event.target.value || 'all'))}>
              <MenuItem value="all">All zones</MenuItem>
              {availableZones.map((zone) => (
                <MenuItem key={zone} value={zone}>
                  {getSeriesLabel(zone)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={(_, value) => value && setTimeRange(value)}
            size="small"
          >
            <ToggleButton value="1h">1H</ToggleButton>
            <ToggleButton value="6h">6H</ToggleButton>
            <ToggleButton value="24h">24H</ToggleButton>
            <ToggleButton value="7d">7D</ToggleButton>
          </ToggleButtonGroup>

        </Box>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 'min(100%, 1000px)' }}>
        For label-quality trends, use Detection Accuracy.
      </Typography>

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
                <Skeleton variant="text" width={120} height={28} />
                <Skeleton variant="text" width={180} />
                <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 2, mt: 1.5 }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          <HeroMetricRow
            kpis={kpis}
            opsQueues={opsQueues}
            groups={groups}
            tableMode="grouped"
            reviewOutcomeRollup={reviewOutcomeRollup}
            onOpenIncidents={() => navigate(buildIncidentsUrl())}
            onHighRiskUnresolved={() => navigate(buildIncidentsUrl({ severity: 'high' }))}
            onRecurringHotspots={() => navigate(buildIncidentsUrl())}
            onReviewThroughput={() => navigate(buildIncidentsUrl({ tab: 'all', table_mode: 'raw', workflow_status: 'Reviewed' }))}
          />

          {showSecondaryMetricRow && (
            <Grid container spacing={1.25} sx={{ mb: 2 }}>
              {secondaryMetricCards.map((card) => (
                <Grid item xs={12} sm={6} lg={3} key={card.key}>
                  <Paper
                    variant="outlined"
                    onClick={() => navigate(buildIncidentsUrl(card.params))}
                    sx={{
                      p: 1.15,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      borderColor:
                        card.tone === 'danger'
                          ? alpha('#c62828', 0.18)
                          : card.tone === 'warning'
                            ? alpha('#d97706', 0.18)
                            : alpha('#94a3b8', 0.28),
                      bgcolor:
                        card.tone === 'danger'
                          ? alpha('#c62828', 0.03)
                          : card.tone === 'warning'
                            ? alpha('#d97706', 0.03)
                            : '#fcfcfd',
                      transition: 'transform 140ms ease, box-shadow 140ms ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 10px 18px rgba(15, 23, 42, 0.05)',
                      },
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontSize: 11 }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, mt: 0.15, lineHeight: 1.2 }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2, lineHeight: 1.2 }}>
                      {card.context}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} lg={9}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  height: '100%',
                  minHeight: 380,
                  borderColor: alpha('#1d4f91', 0.22),
                  bgcolor: alpha('#1d4f91', 0.02),
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
                }}
              >
                <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>New risks and resolutions</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1, lineHeight: 1.45, fontSize: 13.5 }}>
                  {operationalReadout}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: 13.5 }}>
                  New risk signals and resolved items over the time range below, with emphasis on recurring signals.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Busiest create window</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {flowHighlights.peakIntake ? `${flowHighlights.peakIntake.created} @ ${flowHighlights.peakIntake.time}` : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Busiest close window</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {peakClose
                        ? `${peakClose.closed} @ ${peakClose.time}`
                        : '—'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ width: '100%', minHeight: 300 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={incidentFlowData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="time" tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{ paddingTop: 6 }} iconType="line" />
                      <Line
                        type="monotone"
                        dataKey="created"
                        name="Created"
                        stroke="#1a56a8"
                        strokeWidth={3}
                        dot={false}
                        strokeLinecap="round"
                      />
                      <Line
                        type="monotone"
                        dataKey="closed"
                        name="Closures"
                        stroke="#047857"
                        strokeWidth={3.5}
                        dot={false}
                        activeDot={{ r: 4, fill: '#047857' }}
                        strokeDasharray="12 7"
                        strokeLinecap="round"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                {!incidentFlowData.length && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No flow data in this range yet.
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} lg={3}>
              <HotspotsCard
                groups={groups}
                zoneNameById={zoneNameById}
                cameraNameById={cameraNameById}
                reviewOutcomeRollup={reviewOutcomeRollup}
                reviewOutcomeHistory={reviewOutcomeHistory}
                ownershipRollup={ownershipRollup}
                correctiveActionRollup={correctiveActionRollup}
                measurementRollup={measurementRollup}
                actionSnapshotRollup={actionSnapshotRollup}
                escalationRollup={escalationRollup}
                opsQueues={opsQueues}
                onSelectHotspot={(target) => {
                  navigate(buildIncidentsUrl(target.type === 'zone' ? { zone_id: target.id } : { camera_id: target.id }));
                }}
              />
            </Grid>
          </Grid>

          {reviewTrendReadout ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                mb: 2.5,
                borderColor: alpha('#0f766e', 0.22),
                bgcolor: alpha('#ecfeff', 0.75),
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
                Review intelligence trend
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, fontSize: 13.5, maxWidth: 980 }}>
                {reviewTrendReadout}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.8 }}>
                This trend reflects reviewed outcome semantics over time, not raw alert volume.
              </Typography>
              {reviewBoundaryReadout ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
                  {reviewBoundaryReadout}
                </Typography>
              ) : null}
              {measurementBoundaryReadout ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                  {measurementBoundaryReadout}
                </Typography>
              ) : null}
            </Paper>
          ) : null}

          <Box sx={{ mt: 1, mb: 2.5 }}>
            <RecurringRiskHeatmapCard
              data={heatmap}
              dimension={heatmapDimension}
              incidentType={heatmapIncidentType}
              incidentTypeOptions={heatmapIncidentTypeOptions}
              zoneNameById={zoneNameById}
              cameraNameById={cameraNameById}
              onDimensionChange={setHeatmapDimension}
              onIncidentTypeChange={setHeatmapIncidentType}
              onCellSelect={({ dimension, id, hour }) => {
                const params: Record<string, string> = { table_mode: 'raw' };
                if (heatmapIncidentType) params.incident_type = heatmapIncidentType;
                if (dimension === 'zone' && id !== '__unassigned__') params.zone_id = id;
                if (dimension === 'camera' && id !== '__unassigned__') params.camera_id = id;
                params.heatmap_hour = String(hour);
                navigate(buildIncidentsUrl(params));
              }}
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2.5,
              mb: 2.5,
              borderColor: alpha('#64748b', 0.22),
              bgcolor: alpha('#f8fafc', 0.65),
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>Coverage by pack</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, mb: 1.5, maxWidth: 900, fontSize: 13 }}>
              Understand which policy packs are active, how many policies they contain, and which packs are driving open
              incidents.
            </Typography>

            <Grid container spacing={1} sx={{ mb: 2 }}>
              {[
                { label: 'Packs with policies', value: packCoverage.kpis.packsWithPolicies },
                { label: 'Active policies', value: packCoverage.kpis.activePolicies },
                { label: 'Packs with open incidents', value: packCoverage.kpis.packsWithOpenIncidents },
                { label: 'Policies triggered in window', value: packCoverage.kpis.policiesTriggeredInWindow },
              ].map((kpi) => (
                <Grid item xs={6} sm={3} key={kpi.label}>
                  <Box
                    sx={{
                      borderRadius: 1.5,
                      px: 1.1,
                      py: 0.85,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: alpha('#94a3b8', 0.25),
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, fontWeight: 700 }}>
                      {kpi.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 17, lineHeight: 1.15, mt: 0.15 }}>{kpi.value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>Open risk groups by pack</Typography>
            {/* TODO: server-side pack rollups would tighten the unassigned bucket and avoid policy-list staleness. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: 11.5 }}>
              Each open group is assigned to a pack when its policy appears in your site policy list. Use{' '}
              <strong>Not linked to a pack</strong> when the policy is missing from that list.
            </Typography>
            <Box sx={{ width: '100%', minHeight: 220 }}>
              {packCoverage.openIncidentsByPack.length ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={packCoverage.openIncidentsByPack}
                    layout="vertical"
                    margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                    <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                    <YAxis dataKey="packLabel" type="category" width={148} tickLine={false} axisLine={false} />
                    <Tooltip formatter={(value: number) => [value, 'Open groups in queue']} />
                    <Bar dataKey="openCount" radius={[0, 4, 4, 0]} fill="#64748b">
                      {packCoverage.openIncidentsByPack.map((entry) => (
                        <Cell
                          key={entry.packKey}
                          style={{ cursor: 'pointer' }}
                          fill={entry.packKey === PACK_COVERAGE_UNKNOWN_KEY ? '#94a3b8' : '#475569'}
                          onClick={() =>
                            navigate(
                              buildIncidentsUrl({
                                table_mode: 'grouped',
                                policy_pack: entry.packKey,
                              }),
                            )
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No open risk groups in this window for the selected area filter.
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              Click a bar to open Incident Operations filtered by pack for the same date range.
            </Typography>

            <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 2, mb: 0.75 }}>Pack summary</Typography>
            <Table size="small" sx={{ '& td, & th': { fontSize: 12.5, py: 0.85 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Pack</TableCell>
                  <TableCell align="right">Policies active / total</TableCell>
                  <TableCell align="right">Open groups</TableCell>
                  <TableCell align="right">Recurring risk areas</TableCell>
                  <TableCell>Last activity</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packCoverage.drilldownRows.map((row) => (
                  <TableRow key={row.packKey}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.packLabel}</TableCell>
                    <TableCell align="right">
                      {row.packKey === PACK_COVERAGE_UNKNOWN_KEY ? '—' : `${row.policiesActive} / ${row.policiesTotal}`}
                    </TableCell>
                    <TableCell align="right">{row.openIncidentGroups}</TableCell>
                    <TableCell align="right">{row.recurringOrHotspots}</TableCell>
                    <TableCell>
                      {row.lastTriggerAt
                        ? new Date(row.lastTriggerAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        sx={{ fontSize: 12, mr: 1.25 }}
                        onClick={() =>
                          navigate(
                            buildIncidentsUrl({
                              table_mode: 'grouped',
                              policy_pack: row.packKey,
                            }),
                          )
                        }
                      >
                        View incidents
                      </Link>
                      {row.packKey !== PACK_COVERAGE_UNKNOWN_KEY ? (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          sx={{ fontSize: 12 }}
                          onClick={() => navigate(`/policies?pack=${encodeURIComponent(row.packKey)}`)}
                        >
                          View policies
                        </Link>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!packCoverage.drilldownRows.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No pack activity in this window.
              </Typography>
            )}
          </Paper>

          {(showIncidentTypeMix || showSeverityMix) && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {showIncidentTypeMix ? (
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3, height: '100%', opacity: 0.95 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Incident Type Mix (secondary)</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: 13 }}>
                      Whether one type dominates; neutral palette, not operational urgency.
                    </Typography>
                    <ResponsiveContainer width="100%" height={168}>
                      <PieChart>
                        <Pie
                          data={incidentTypeBreakdown}
                          cx="50%"
                          cy="50%"
                          innerRadius={32}
                          outerRadius={60}
                          paddingAngle={4}
                          dataKey="value"
                          nameKey="label"
                        >
                          {incidentTypeBreakdown.map((entry, index) => (
                            <Cell
                              key={entry.name}
                              fill={INCIDENT_TYPE_PIE_COLORS[index % INCIDENT_TYPE_PIE_COLORS.length]}
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(buildIncidentsUrl({ incident_type: entry.name }))}
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number, _name: string, item: any) => [value, item?.payload?.label || 'Incident type']} />
                        <Legend formatter={(value: string) => value} />
                      </PieChart>
                    </ResponsiveContainer>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Click a segment to open filtered incidents.
                    </Typography>
                  </Paper>
                </Grid>
              ) : null}

              {showSeverityMix ? (
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3, height: '100%', opacity: 0.95 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Severity Mix (secondary)</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: 13 }}>
                      Pressure by severity; uses urgency colors.
                    </Typography>
                    <ResponsiveContainer width="100%" height={168}>
                      <BarChart data={severityBreakdown} layout="vertical" margin={{ top: 4, right: 12, left: 12, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis dataKey="label" type="category" width={92} />
                        <Tooltip formatter={(value: number, _name: string, item: any) => [`${value} (${item?.payload?.percent || 0}%)`, 'Severity']} />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                          {severityBreakdown.map((entry, index) => (
                            <Cell
                              key={entry.name}
                              fill={
                                entry.name === 'critical'
                                  ? '#c62828'
                                  : entry.name === 'high'
                                    ? '#e36d23'
                                    : entry.name === 'medium'
                                      ? '#d9a10d'
                                      : getZoneColor(entry.name, index)
                              }
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(buildIncidentsUrl({ severity: entry.name }))}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Click a bar to open filtered incidents.
                    </Typography>
                  </Paper>
                </Grid>
              ) : null}
            </Grid>
          )}

          {hasMeaningfulTelemetry && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, height: '100%', borderStyle: 'dashed' }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Operational telemetry</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: 12.5 }}>
                    Live signals, separate from incident queue counts.
                  </Typography>
                  <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
                    {telemetryCards.map((card) => (
                      <Grid item xs={12} sm={6} key={card.label}>
                        <Box sx={{ borderRadius: 1.5, bgcolor: 'background.default', p: 1.25 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{card.label}</Typography>
                          <Typography sx={{ fontWeight: 700, mt: 0.25, fontSize: 14 }}>{card.value}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, fontSize: 12, lineHeight: 1.35 }}>
                            {card.description}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={filteredZoneData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="time" tickLine={false} axisLine={false} />
                      <YAxis tickLine={false} axisLine={false} />
                      <Tooltip />
                      <Legend />
                      {occupancyKeys.map((key, index) => (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={getSeriesLabel(key)}
                          stroke={getZoneColor(key, index)}
                          strokeWidth={2.5}
                          dot={filteredZoneData.length <= 1}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                  {!occupancyKeys.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No live occupancy history is available for this scope yet.
                    </Typography>
                  )}
                  {occupancySignalFlatZero && (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                      Occupancy is flat at 0 for every visible zone. If people are present, verify occupancy publishing or zone-to-camera wiring.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}

          <Paper
            variant="outlined"
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 2,
              mt: 2.5,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.25,
              borderColor: alpha('#94a3b8', 0.3),
              bgcolor: alpha('#f1f5f9', 0.85),
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                component="span"
                sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                Scope
              </Typography>
              <Typography component="span" variant="body2" sx={{ fontSize: 13, fontWeight: 600 }}>
                {selectedZone === 'all' ? 'All zones' : getSeriesLabel(selectedZone)}
              </Typography>
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                · {timeRangeLabel} · {overviewMetrics.openZones} open {overviewMetrics.openZones === 1 ? 'area' : 'areas'}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                pt: { xs: 0.75, sm: 0 },
                pl: { xs: 0, sm: 1.5 },
                borderTop: { xs: 1, sm: 0 },
                borderLeft: { xs: 0, sm: 1 },
                borderColor: 'divider',
              }}
            >
              <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={() => navigate(buildIncidentsUrl())}
                sx={{ textTransform: 'none', fontSize: 12.5, py: 0.5, px: 1.25, minWidth: 0 }}
              >
                Open incidents
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(buildIncidentsUrl({ severity: 'high' }))}
                sx={{ textTransform: 'none', fontSize: 12.5, py: 0.5, px: 1.25, minWidth: 0, borderColor: alpha('#64748b', 0.4) }}
              >
                High-risk review
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default Analytics;
